

































import { Component, Prop, Vue, Watch } from "@/shared/vuepack";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import Pagination from "@/shared/components/ui/Pagination.vue";
import { formatCurrency } from "@/shared/currency";
import moment from "moment";
import { useBankAccountStore } from "@/employee/store/bankAccount";
import { usePersonalContributionSchedulesStore } from "@/shared/store/personalContributionSchedules";
import { useHsaStore } from "@/shared/store/hsa";
import {
  ContributionType,
  Transaction,
  TransactionSource,
  ContributionActivity,
  TransactionApiStatus
  } from "@/shared/types";
import Pill from "@/shared/components/ui/Pill.vue";
import CurrencyAmount from "@/shared/components/ui/CurrencyAmount.vue";
import _ from "lodash";
import { useHsaTotalStore } from "@/shared/store/hsaTotal";
import { formatMMMDateString } from "@/shared/dateString";
import ContributionActivityRow from "@/shared/components/contributionsV3/ContributionActivityRow.vue";

interface ContributionRow {
  date: string;
  amount: string;
  description: string;
  subDescription: string;
  status: string;
  statusReason: string;
  type: ContributionType;
}

@Component ({
  components: {
    CurrencyAmount,
    Pill,
    GenericLoadingSpinner,
    Pagination,
    ContributionActivityRow
  }
})
export default class ContributionsOverview extends Vue {
  hsaStore = useHsaStore();
  bankAccountStore = useBankAccountStore();
  contributionSchedulesStore = usePersonalContributionSchedulesStore();
  hsaTotalStore = useHsaTotalStore();

  formatCurrency = formatCurrency;
  contributionType = ContributionType.All;

  contributionTypeSelectOptions = [
    { value: ContributionType.All, text: "All" },
    { value: ContributionType.PostTax, text: "Post-Tax" },
    { value: ContributionType.EmployeePayroll, text: "Employee Payroll" },
    { value: ContributionType.EmployerPayroll, text: "Employer Payroll" }
  ];


  @Prop({required: true})
  page: number;

  @Prop({required: false})
  username: string;

  @Prop({required: false})
  partnerId: number;

  mounted (): void {
    const args = { username: this.username, partnerId: this.partnerId };
    this.bankAccountStore.loadLinkedBankAccounts();

    // load future contributions
    this.contributionSchedulesStore.getFutureContributions(args);

    // load upcoming transactions
    this.hsaTotalStore.getUserPayrollSummary();

    this.loadPage();
  }

  get isLoaded (): boolean {
    return this.bankAccountStore.linkedAccountsLoaded
        && this.contributionSchedulesStore.isFutureContributionsLoaded
        && this.hsaStore.isAllContributionActivityPageLoaded(this.page, this.contributionType)
        && this.hsaTotalStore.userPayrollSummaryIsLoaded;
  }

  @Watch("contributionType")
  onContributionTypeChange () {
    if (this.page !== 1) {
      this.switchPage (1);
    }
    else {
      this.loadPage();
    }
  }

  @Watch("page")
  loadPage () {
    this.hsaStore.loadAllContributionActivity({
      page: this.page,
      username: this.username,
      partnerId: this.partnerId,
      contributionType: this.contributionType
    });
  }

  get isTableEmpty () {
    return this.isLoaded && this.contributionRows.length === 0;
  }

   get contributionRows (): ContributionRow[] {
    if (!this.isLoaded) {
      return [];
    }

    const results = [];

    if (this.page === 1) {
      // upcoming
      results.push(...this.upcomingContributions);
    }

    // others (pending, posted, rejected)
    results.push(...this.hsaStore.allContributionActivityForPage(this.page, this.contributionType)
      .map(x => {
        return {
          date: this.getFormattedDate(x.contributionDate),
          amount: x.amount,
          description: x.displayDescription,
          subDescription: this.getSubDescription(x),
          status: this.getStatus(x.status),
          statusReason: x.displayRejectionReason,
          type: this.getContributionType(x.source)
        };
    }));

    return results;
  }

  // Get the next upcoming transaction (or transactions if they fall on the same date)
  get upcomingContributions (): ContributionRow[] {
    const futurePersonalContributions = _.orderBy(this.contributionSchedulesStore.allFutureContributions, ["calendarDate"], ["asc"]);
    const upcomingPayrollFundings = _.orderBy(this.upcomingPayrollFundingTransactions, ["postedDate"], ["asc"]);

    const dates = [futurePersonalContributions[0] ? futurePersonalContributions[0].calendarDate : null,
      upcomingPayrollFundings[0] ? upcomingPayrollFundings[0].postedDate : null];
    if (dates.filter(d => d).length === 0) {
      return [];
    }

    const nextDate = moment.min(dates.filter(d => d).map(d => moment(d))).format("YYYY-MM-DD");

    const upcoming = [];
    // Return all contributions on that date
    upcoming.push(...futurePersonalContributions.filter(f => moment(f.calendarDate).format("YYYY-MM-DD") === nextDate).map(contribution => {
      return {
        date: this.getFormattedDate(contribution.calendarDate),
        amount: contribution.amount,
        description: `Contribution ${moment(contribution.calendarDate).year()}`,
        subDescription: this.getAccountName(contribution.linkedExternalBankAccountId),
        status: "Upcoming",
        type: ContributionType.PostTax
      };
    }));

    upcoming.push(...upcomingPayrollFundings.filter(f => moment(f.postedDate).format("YYYY-MM-DD") === nextDate).map(transaction => {
      return {
        date: this.getFormattedDate(transaction.postedDate),
        amount: transaction.amount,
        description: transaction.displayDescription,
        subDescription: transaction.displaySource,
        status: "Upcoming",
        type: transaction.displaySource === "Employer Payroll"
            ? ContributionType.EmployerPayroll : ContributionType.EmployeePayroll
      };
    }));

    return upcoming.filter(x => x.type === this.contributionType || this.contributionType === ContributionType.All);
  }

  get upcomingPayrollFundingTransactions (): Transaction[] {
    const transactions: Transaction[] = [];

    if (this.hsaTotalStore.nextPayrollFunding && this.hsaTotalStore.nextPayrollFunding.employeeRecurringAmount) {
      transactions.push({
        postedDate: this.hsaTotalStore.nextPayrollFunding.payrollDate,
        source: null,
        displaySource: "Employee Payroll",
        description: "Employee Payroll",
        amount: this.hsaTotalStore.nextPayrollFunding.employeeRecurringAmount,
        moneyIncreasing: true,
        displayDescription: "Pay Period Contribution",
        status: null,
        displayStatus: "Upcoming",
        linkedBankAccount: null
      });
    }

    if (this.hsaTotalStore.nextPayrollFunding && this.hsaTotalStore.nextPayrollFunding.employerAmount) {
      transactions.push({
        postedDate: this.hsaTotalStore.nextPayrollFunding.payrollDate,
        source: null,
        displaySource: "Employer Payroll",
        description: "Employer Payroll",
        amount: this.hsaTotalStore.nextPayrollFunding.employerAmount,
        moneyIncreasing: true,
        displayDescription: "Pay Period Contribution",
        status: null,
        displayStatus: "Upcoming",
        linkedBankAccount: null
      });
    }

    if (this.hsaTotalStore.nextPayrollFunding && this.hsaTotalStore.nextPayrollFunding.employeeOneTimeAmount) {
      transactions.push({
        postedDate: this.hsaTotalStore.nextPayrollFunding.payrollDate,
        source: null,
        displaySource: "One-Time Lift",
        description: "One-Time Lift",
        amount: this.hsaTotalStore.nextPayrollFunding.employeeOneTimeAmount,
        moneyIncreasing: true,
        displayDescription: "Pay Period Contribution",
        status: null,
        displayStatus: "Upcoming",
        linkedBankAccount: null
      });
    }

    return transactions;
  }

  getFormattedDate (date?: string): string {
    return date && formatMMMDateString(date) || "None";
  }

  getAccountName (accountId: number | string): string {
    const index = this.bankAccountStore.verifiedLinkedAccounts.findIndex(x => x.linkedBankAccountId === accountId);
    if (index < 0) {
      return accountId.toString();
    }
    const accountNumber = this.bankAccountStore.verifiedLinkedAccounts[index].bankAccountNumber;

    return `SOURCE: ${this.bankAccountStore.verifiedLinkedAccounts[index].bankAccountName}
      ENDING IN ${accountNumber.substring(accountNumber.length - 4)}`;
  }

  getContributionType (source: TransactionSource): ContributionType {
    switch (source) {
      case TransactionSource.PayrollEmployer:
        return ContributionType.EmployerPayroll;
      case TransactionSource.PayrollRecurring:
      case TransactionSource.PayrollOneTime:
        return ContributionType.EmployeePayroll;
      case TransactionSource.PersonalContribution:
      case TransactionSource.PayrollPostTax:
        return ContributionType.PostTax;
    }
  }

  getSubDescription (item: ContributionActivity): string {
    if (item.displaySource) {
      return item.displaySource;
    }

    switch (item.type) {
      case TransactionSource.PayrollEmployer:
        return "Employer Payroll";
      case TransactionSource.PayrollRecurring:
      case TransactionSource.PayrollOneTime:
        return "Employee Payroll";
      default:
        return "";
    }
  }

  getStatus (status: TransactionApiStatus): string {
    switch (status) {
      case TransactionApiStatus.Complete:
        return "Contributed";
      case TransactionApiStatus.Rejected:
        return "Failed";
      case TransactionApiStatus.Processing:
        return "Pending";
      default:
        return "";
    }
  }

  getPillType (item: ContributionRow): string {
    switch (item.status) {
      case "Contributed":
        return "green-bg";
      case "Pending":
        return "warning-pill";
      case "Failed":
        return "red-bg";
      default:
        return "grey-bg";
    }
  }

  switchPage (newPage) {
    this.$router.push({query: {...this.$route.query, activityPage: newPage}});
  }

  get pageCount (): number {
    return this.hsaStore.allContributionActivityPageCount(this.page, this.contributionType);
  }
}
